import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Stack } from '@mui/material';

import dayjs from 'dayjs';

import { ReactComponent as CopyIcon } from '@/assets/icons/copy-blue-icon.svg';
import { ReactComponent as LinkExternalIcon } from '@/assets/icons/link-external-blue-icon.svg';
import { ReactComponent as ShipmentIcon } from '@/assets/icons/shipment-truck-icon.svg';
import { useCopyToClipboard } from '@/shared/lib/hooks/useCopyToClipboard';
import AppButton from '@/shared/ui/AppButton';

import { AwbNumberText, DetailLabel, DetailValue, SectionCard } from '../styles';
import AwbNotice from './AwbNotice';
import AwbSection from './AwbSection';

export interface ShipmentSummaryProps {
  awbNumber?: string;
  verifying?: boolean;
  failed?: boolean;
  trackingUrl?: string;
  pickupDate?: string;
  senderName?: string;
  parcels: number;
  weight?: number;
  dimensions?: string;
  recipientName?: string;
  recipientLocality?: string;
  total?: number;
}

const Detail: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Stack gap="0.5rem" minWidth={0}>
    <DetailLabel>{label}</DetailLabel>
    <DetailValue>{children}</DetailValue>
  </Stack>
);

const formatNumber = (value: number): string => String(Math.round(value * 100) / 100);

const ShipmentSummary: FC<ShipmentSummaryProps> = ({
  awbNumber,
  verifying,
  failed,
  trackingUrl,
  pickupDate,
  senderName,
  parcels,
  weight,
  dimensions,
  recipientName,
  recipientLocality,
  total,
}) => {
  const { t } = useTranslation();
  const { copyText } = useCopyToClipboard();

  return (
    <Stack gap="1rem" data-testid="awb-result">
      {verifying && <AwbNotice>{t('awb.verifying_hint')}</AwbNotice>}
      {failed && <AwbNotice data-testid="awb-result-failed">{t('awb.not_created_hint')}</AwbNotice>}

      <Stack direction="row" gap="1rem" alignItems="stretch">
        <SectionCard
          sx={{ flex: '1 0 0', alignSelf: 'stretch', minWidth: 0, justifyContent: 'center' }}
          data-testid="awb-result-number-card"
        >
          <DetailLabel>{t('awb.column_awb_number')}</DetailLabel>
          <Stack direction="row" alignItems="center" gap="1rem" minWidth={0}>
            <AwbNumberText noWrap data-testid="awb-result-number">
              {awbNumber ?? t('awb.pending_number')}
            </AwbNumberText>
            {!!awbNumber && (
              <AppButton
                variant="secondary"
                size="small"
                startIcon={<CopyIcon />}
                onClick={() => copyText(awbNumber)}
                data-testid="awb-result-copy"
              >
                {t('awb.copy')}
              </AppButton>
            )}
          </Stack>
        </SectionCard>

        <SectionCard
          sx={{ flex: '1 0 0', minWidth: 0, justifyContent: 'center', alignItems: 'flex-start' }}
          data-testid="awb-result-tracking-card"
        >
          <DetailLabel>{t('awb.tracking')}</DetailLabel>
          {trackingUrl ? (
            <AppButton
              variant="link"
              size="small"
              endIcon={<LinkExternalIcon />}
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="awb-result-tracking"
            >
              {t('Track parcel')}
            </AppButton>
          ) : (
            <DetailValue>{t('awb.tracking_unavailable')}</DetailValue>
          )}
        </SectionCard>
      </Stack>

      <AwbSection
        icon={<ShipmentIcon width={24} height={24} />}
        title={t('awb.shipment')}
        data-testid="awb-result-shipment"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
          }}
        >
          <Detail label={t('Pickup')}>
            {[pickupDate ? dayjs(pickupDate).format('ddd, D MMM YYYY') : undefined, senderName]
              .filter(Boolean)
              .join(', ') || '-'}
          </Detail>
          <Detail label={t('awb.parcels_weight')}>
            {weight ? `${parcels} x ${formatNumber(weight / Math.max(parcels, 1))} KG` : '-'}
          </Detail>
          {!!dimensions && <Detail label={t('awb.review_dimensions')}>{dimensions}</Detail>}
          <Detail label={t('Recipient')}>
            {[recipientName, recipientLocality].filter(Boolean).join(', ') || '-'}
          </Detail>
          <Detail label={t('awb.review_total_weight')}>
            {weight ? `${formatNumber(weight)} KG` : '-'}
          </Detail>
          <Detail label={t('awb.total_charged')}>
            {total !== undefined && total !== null ? `${total.toFixed(2)} RON` : '-'}
          </Detail>
        </Box>
      </AwbSection>
    </Stack>
  );
};

export default ShipmentSummary;
